'use client';

import { useMemo, useRef, useState } from 'react';
import {
  BuildingIcon,
  HousingCard,
  HousingCardSkeleton,
  HouseKeyIcon,
  IconToggleGroup,
  NoData,
} from '@/components';
import { CardsGrid, Container, ListTitle } from './styles';
import { useGetHousingList } from '@/queris';
import { CustomPagination } from '@/components';
import { OfferType } from '@/types';
import { useTranslations } from 'next-intl';
import { keepPreviousData } from '@tanstack/react-query';
import { formatNumberForDisplay } from '@/common';

const ITEMS_PER_PAGE = 10;

type BrokerTab = { kind: 'offerType'; code: OfferType } | { kind: 'project' };

interface BrokerOfficesGridProps {
  brokerName: string;
  officeId?: number;
  sellCount: number;
  rentCount: number;
  projectCount: number;
}

export const BrokerOfficesGrid = ({
  brokerName,
  officeId,
  sellCount,
  rentCount,
  projectCount,
}: BrokerOfficesGridProps) => {
  const t = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);

  const firstAvailableTab: BrokerTab =
    sellCount > 0
      ? { kind: 'offerType', code: OfferType.SELL }
      : rentCount > 0
        ? { kind: 'offerType', code: OfferType.RENT }
        : { kind: 'project' };

  const [activeTab, setActiveTab] = useState<BrokerTab>(firstAvailableTab);
  const [currentPage, setCurrentPage] = useState(0);

  const isProject = activeTab.kind === 'project';
  const offerType = activeTab.kind === 'offerType' ? activeTab.code : undefined;

  const requestFilters = useMemo(
    () =>
      officeId
        ? {
            officeId,
            currentPageIndex: currentPage,
            itemsPerPage: ITEMS_PER_PAGE,
            ...(isProject ? { project: true, itemsPerProject: 3 } : { offerType, project: false }),
          }
        : undefined,
    [officeId, currentPage, isProject, offerType],
  );

  const { data, isLoading, isPending } = useGetHousingList(requestFilters, {
    enabled: Boolean(officeId),
    placeholderData: keepPreviousData,
  });

  const objects = data?.objects ?? [];
  const numFound = data?.numFound ?? 0;

  const handlePageChange = (newPage: number) => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setCurrentPage(newPage);
  };

  const handleTabChange = (tab: BrokerTab) => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  const tabs: { value: BrokerTab; title: string; icon?: React.ReactNode; count: number }[] = [
    sellCount > 0 && {
      value: { kind: 'offerType', code: OfferType.SELL },
      title: t('sell'),
      count: sellCount,
    },
    rentCount > 0 && {
      value: { kind: 'offerType', code: OfferType.RENT },
      title: t('rent'),
      icon: <HouseKeyIcon />,
      count: rentCount,
    },
    projectCount > 0 && {
      value: { kind: 'project' },
      title: t('navigation.projects'),
      icon: <BuildingIcon />,
      count: projectCount,
    },
  ].filter(Boolean) as { value: BrokerTab; title: string; icon?: React.ReactNode; count: number }[];

  const isActiveTab = (tab: BrokerTab) => {
    if (tab.kind !== activeTab.kind) return false;
    if (tab.kind === 'offerType' && activeTab.kind === 'offerType') {
      return tab.code === activeTab.code;
    }
    return true;
  };

  const shouldShowSkeletons = isLoading || isPending;
  const shouldShowCards = !shouldShowSkeletons && objects.length > 0;

  const itemsTotalCountFormatted = formatNumberForDisplay(sellCount + rentCount + projectCount);
  const listTitle = `${itemsTotalCountFormatted} ${t('brokerOfficesDetails.officeListTitle')} ${brokerName}`;

  return (
    <Container ref={containerRef}>
      <ListTitle variant="h2">{listTitle}</ListTitle>

      {tabs.length > 1 && (
        <IconToggleGroup
          options={tabs.map((tab) => ({
            value: tab.value,
            title: tab.title,
            icon: tab.icon,
            count: tab.count,
          }))}
          selected={tabs.filter((tab) => isActiveTab(tab.value)).map((tab) => tab.value)}
          onChange={(values) => {
            if (values.length === 0) return;
            handleTabChange(values[values.length - 1]);
          }}
          isEqual={(a, b) => {
            if (a.kind !== b.kind) return false;
            if (a.kind === 'offerType' && b.kind === 'offerType') return a.code === b.code;
            return true;
          }}
        />
      )}

      <CardsGrid>
        {shouldShowCards &&
          objects.map((object, index) => (
            <HousingCard
              key={object.id}
              id={`card-${object.id}`}
              data={object}
              fromList
              offerType={offerType}
              priority={index === 0}
              isProject={isProject}
            />
          ))}
        {shouldShowSkeletons &&
          Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
            <HousingCardSkeleton key={`skeleton-${index}`} />
          ))}
        {!shouldShowCards && !shouldShowSkeletons && <NoData />}
      </CardsGrid>

      <CustomPagination
        page={currentPage}
        numFound={numFound}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={handlePageChange}
      />
    </Container>
  );
};
